import { isUnconfirmedAccrualAccountLine } from "@common/helpers/accrualAccountConfirmation";
import {
    ExtractionStatus,
    FinancialDocumentExtended,
    FinancialDocumentListItem,
    FinancialDocumentListItemAccrualExtractionStatus,
    ImporterErrors,
    TransactionsImportStatus,
} from "@common/types/financialDocument";
import {
    IMPORTER_BILLS_QUEUE,
    IMPORTER_INVOICES_QUEUE,
    NON_BLOCKING_IMPORTER_ERRORS,
} from "@common/helpers/financialDocument";
import {
    getInformationalImporterErrors,
    ImporterDisplayError,
    ImporterDisplayErrorKind,
    IMPORTER_ERROR_PRIORITY,
} from "@components/importer/helpers";
import { DocumentBillsExtractionRecord } from "@common/schemas/financialDocument/document-bills-extraction-record.schema";
import { DocumentInvoicesExtractionRecord } from "@common/schemas/financialDocument/document-invoices-extraction-record.schema";
import { BillExtractionRecord } from "@common/schemas/financialDocument/bill-extraction-record.schema";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import { InvoiceExtractionLineItem } from "@common/schemas/financialDocument/invoice-extraction-line-item.schema";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import {
    AccrualImporterDocumentRow,
    AccrualImporterViewRow,
} from "./accrualImporter.types";

export function resolveAccrualCounterpartyIdByName({
    counterpartyName,
    counterpartiesMap,
}: {
    counterpartyName: string | null | undefined;
    counterpartiesMap: Map<string, string>;
}): string | null {
    if (!counterpartyName) {
        return null;
    }

    const normalizedName = counterpartyName.toLowerCase();
    for (const [id, name] of counterpartiesMap) {
        if (name.toLowerCase() === normalizedName) {
            return id;
        }
    }

    return null;
}

export function getAccrualExtraction(
    document: FinancialDocumentExtended,
): DocumentInvoicesExtractionRecord | DocumentBillsExtractionRecord | null;
export function getAccrualExtraction(
    document: FinancialDocumentListItem | FinancialDocumentExtended,
):
    | FinancialDocumentListItemAccrualExtractionStatus
    | DocumentInvoicesExtractionRecord
    | DocumentBillsExtractionRecord
    | null;
export function getAccrualExtraction(
    document: FinancialDocumentListItem | FinancialDocumentExtended,
):
    | FinancialDocumentListItemAccrualExtractionStatus
    | DocumentInvoicesExtractionRecord
    | DocumentBillsExtractionRecord
    | null {
    if (document.importerQueue === IMPORTER_INVOICES_QUEUE) {
        return document.documentInvoicesExtraction ?? null;
    }
    if (document.importerQueue === IMPORTER_BILLS_QUEUE) {
        return document.documentBillsExtraction ?? null;
    }
    return (
        document.documentBillsExtraction ??
        document.documentInvoicesExtraction ??
        null
    );
}

export function getAllAccrualLineItemExtractions(
    financialDocument: FinancialDocumentExtended,
    mode: "bills",
): BillExtractionRecord[];
export function getAllAccrualLineItemExtractions(
    financialDocument: FinancialDocumentExtended,
    mode: "invoices",
): InvoiceExtractionRecord[];
export function getAllAccrualLineItemExtractions(
    financialDocument: FinancialDocumentExtended,
    mode: AccrualDocumentImporterMode,
): InvoiceExtractionRecord[] | BillExtractionRecord[];
export function getAllAccrualLineItemExtractions(
    financialDocument: FinancialDocumentExtended,
    mode: AccrualDocumentImporterMode,
): InvoiceExtractionRecord[] | BillExtractionRecord[] {
    if (mode === "bills") {
        return financialDocument.documentBillsExtraction?.billExtractions ?? [];
    }

    return (
        financialDocument.documentInvoicesExtraction?.invoiceExtractions ?? []
    );
}

export function mergeUpdatedAccrualExtraction<T extends { id: string }>({
    allExtractions,
    updatedExtraction,
}: {
    allExtractions: T[];
    updatedExtraction: T;
}): T[] {
    if (allExtractions.length === 0) {
        return [updatedExtraction];
    }

    const hasUpdatedExtraction = allExtractions.some(
        (extraction) => extraction.id === updatedExtraction.id,
    );
    if (!hasUpdatedExtraction) {
        return [...allExtractions, updatedExtraction];
    }

    return allExtractions.map((extraction) =>
        extraction.id === updatedExtraction.id ? updatedExtraction : extraction,
    );
}

type AccrualLineItemExtraction = InvoiceExtractionRecord | BillExtractionRecord;

export function getAccrualLineItemExtraction(
    financialDocument: FinancialDocumentExtended,
    mode: "bills",
): BillExtractionRecord | null;
export function getAccrualLineItemExtraction(
    financialDocument: FinancialDocumentExtended,
    mode: "invoices",
): InvoiceExtractionRecord | null;
export function getAccrualLineItemExtraction(
    financialDocument: FinancialDocumentExtended,
    mode: AccrualDocumentImporterMode,
): AccrualLineItemExtraction | null;
export function getAccrualLineItemExtraction(
    financialDocument: FinancialDocumentExtended,
    mode: AccrualDocumentImporterMode,
): AccrualLineItemExtraction | null {
    if (mode === "bills") {
        return (
            financialDocument.documentBillsExtraction?.billExtractions?.[0] ??
            null
        );
    }
    return (
        financialDocument.documentInvoicesExtraction?.invoiceExtractions?.[0] ??
        null
    );
}

export function getAccrualViewRowLineItem(
    row: AccrualImporterViewRow,
    mode: AccrualDocumentImporterMode,
): InvoiceExtractionLineItem | null {
    const financialDocument = row.financialDocument;
    if (!financialDocument || !row.lineItemId) {
        return null;
    }
    const extraction = resolveAccrualRowExtraction(
        financialDocument,
        mode,
        row.extractionId,
    );
    return (
        extraction?.lineItems.find((item, index) => {
            if (item.lineId === row.lineItemId) {
                return true;
            }
            return row.lineItemId === `index:${index}`;
        }) ?? null
    );
}

export function resolveAccrualRowExtraction(
    financialDocument: FinancialDocumentExtended,
    mode: AccrualDocumentImporterMode,
    extractionId?: string,
): InvoiceExtractionRecord | BillExtractionRecord | null {
    const extractions = getAllAccrualLineItemExtractions(
        financialDocument,
        mode,
    );
    if (!extractionId) {
        return extractions[0] ?? null;
    }
    return (
        extractions.find((extraction) => extraction.id === extractionId) ?? null
    );
}

export function isAccrualExtractionReady(document: FinancialDocumentExtended) {
    const extraction = getAccrualExtraction(document);
    return extraction?.status === ExtractionStatus.COMPLETED;
}

export function accrualDocumentBeingProcessed(
    document: FinancialDocumentListItem | FinancialDocumentExtended,
) {
    const extraction = getAccrualExtraction(document);

    if (extraction?.status === ExtractionStatus.FAILED) {
        return false;
    }

    return (
        !extraction ||
        extraction.status === ExtractionStatus.NOT_STARTED ||
        extraction.status === ExtractionStatus.IN_PROGRESS
    );
}

export function accrualDocumentNeedsAttention(
    document: FinancialDocumentExtended,
) {
    const extraction = getAccrualExtraction(document);

    return (
        extraction?.status === ExtractionStatus.FAILED ||
        Boolean(document.importerErrors?.length)
    );
}

export type AccrualDocumentImportStatus =
    | { kind: "uploading" }
    | { kind: "processing" }
    | { kind: "imported" }
    | { kind: "import_scheduled" }
    | { kind: "importing" }
    | { kind: "import_failed_retryable" }
    | { kind: "extraction_failed" }
    | { kind: "review_tasks"; errors: ImporterDisplayError[] }
    | { kind: "ready_with_warnings"; errors: ImporterDisplayError[] }
    | { kind: "ready" };

export function getAccrualDocumentImportStatus(
    row: AccrualImporterDocumentRow,
): AccrualDocumentImportStatus {
    const financialDocument = row.financialDocument;

    if (!financialDocument) {
        return { kind: "uploading" };
    }

    if (accrualDocumentBeingProcessed(financialDocument)) {
        return { kind: "processing" };
    }

    const transactionsImportStatus = financialDocument.transactionsImportStatus;

    if (transactionsImportStatus === TransactionsImportStatus.FINISHED) {
        return { kind: "imported" };
    }

    if (transactionsImportStatus === TransactionsImportStatus.SCHEDULED) {
        return { kind: "import_scheduled" };
    }

    if (transactionsImportStatus === TransactionsImportStatus.IN_PROGRESS) {
        return { kind: "importing" };
    }

    const extraction = getAccrualExtraction(financialDocument);
    if (extraction?.status === ExtractionStatus.FAILED) {
        return { kind: "extraction_failed" };
    }

    const blockingErrors = getAccrualBlockingErrors(financialDocument);
    if (blockingErrors.length > 0) {
        return { kind: "review_tasks", errors: blockingErrors };
    }

    if (!isAccrualExtractionReady(financialDocument)) {
        return { kind: "processing" };
    }

    if (transactionsImportStatus === TransactionsImportStatus.FAILED) {
        return { kind: "import_failed_retryable" };
    }

    const warnings = getInformationalImporterErrors(financialDocument);
    if (warnings.length > 0) {
        return { kind: "ready_with_warnings", errors: warnings };
    }

    return { kind: "ready" };
}

export function isAccrualRowReadyForImport(row: AccrualImporterDocumentRow) {
    const status = getAccrualDocumentImportStatus(row);

    return (
        status.kind === "ready" ||
        status.kind === "ready_with_warnings" ||
        status.kind === "import_failed_retryable"
    );
}

export function nextAccrualDraftToReviewRowId({
    rows,
    currentRowId,
}: {
    rows: AccrualImporterDocumentRow[];
    currentRowId: string;
}): string | undefined {
    const currentIndex = rows.findIndex((row) => row.id === currentRowId);
    const nextRow = rows.slice(currentIndex + 1).find((row) => {
        const status = getAccrualDocumentImportStatus(row);
        return (
            status.kind === "ready" ||
            status.kind === "ready_with_warnings" ||
            status.kind === "extraction_failed" ||
            status.kind === "review_tasks" ||
            status.kind === "import_failed_retryable"
        );
    });
    return nextRow?.id;
}

export function getAccrualImporterFileName(row: AccrualImporterDocumentRow) {
    return row.financialDocument?.fileName ?? row.file?.name;
}

function documentHasUnconfirmedAccrualAccount(
    financialDocument: FinancialDocumentExtended,
): boolean {
    const extractions = [
        ...(financialDocument.documentInvoicesExtraction?.invoiceExtractions ??
            []),
        ...(financialDocument.documentBillsExtraction?.billExtractions ?? []),
    ];
    return extractions.some((extraction) =>
        extraction.lineItems.some((line) =>
            isUnconfirmedAccrualAccountLine({
                amount: line.amount,
                accountId: line.accountId,
                accountIdSource: line.accountIdSource,
            }),
        ),
    );
}

function getAccrualBlockingErrors(
    financialDocument: FinancialDocumentExtended,
): ImporterDisplayError[] {
    const storedErrors = (financialDocument.importerErrors ?? []).filter(
        (error) => !NON_BLOCKING_IMPORTER_ERRORS.includes(error),
    );
    const errors =
        documentHasUnconfirmedAccrualAccount(financialDocument) &&
        !storedErrors.includes(ImporterErrors.ACCRUAL_UNCONFIRMED_ACCOUNT)
            ? [...storedErrors, ImporterErrors.ACCRUAL_UNCONFIRMED_ACCOUNT]
            : storedErrors;

    return errors
        .toSorted(
            (a, b) => IMPORTER_ERROR_PRIORITY[a] - IMPORTER_ERROR_PRIORITY[b],
        )
        .map((error) => ({
            kind: ImporterDisplayErrorKind.IMPORTER_EXTRACTION,
            error,
        }));
}

export function buildAccrualImporterViewRows({
    sortedRows,
    emptyRowsNumber,
}: {
    sortedRows: AccrualImporterDocumentRow[];
    emptyRowsNumber: number;
}): AccrualImporterViewRow[] {
    const dataRows = sortedRows.map((row, index) => ({
        id: row.id,
        rowNumber: index + 1,
        file: row.file,
        financialDocument: row.financialDocument,
    }));

    const placeholderRows = Array.from(
        { length: emptyRowsNumber },
        (_, index) => ({
            id: `placeholder-${index}`,
            rowNumber: sortedRows.length + index + 1,
            isPlaceholder: true,
        }),
    );

    return [...dataRows, ...placeholderRows];
}
